// Dashboard Portal
// pages/Steps/SignUpSteps.js (UPDATED WITH TERMS)

import React, { useState, useEffect, useRef, useCallback } from "react";
import ProfilePage from "../UserProfile/Profile";
import CreatePrompts from "../../../components/CreatePrompts";
import PricingPage from "../Billing/PricingPage";
import TermsAcceptanceStep from "../Terms/TermsAcceptanceStep";
import {
  Stepper, Step, StepLabel, Box, Typography, Button, CircularProgress, Alert, useTheme,
} from "@mui/material";
import { useNavigate, useSearchParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';

import brandingBg from '../../../assets/images/bot-avatar.jpg';
import { SETUP_COMPLETE_LOCAL_KEY } from "../../../config";

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

// Role-based redirect after setup completion
const getDefaultRoute = () => {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const role = user?.active_team?.user_role_in_team?.toLowerCase();
    if (role === 'student') return '/student-portal/dashboard';
    if (role === 'customer') return '/customer-panel/dashboard';
    return '/chatbots';
};

const colors = {
  sidebarBg: '#1e293b',
  sidebarHover: '#313d4a',
  sidebarText: '#dee4ee',
  sidebarTextMuted: '#8a99af',
  accent: '#3b82f6',
  textPrimary: '#1e293b',
  textSecondary: '#475569',
  textTertiary: '#94a3b8',
  success: '#059669',
};

// KlonIT Logo SVG
const KlonITLogo = ({ color = "white", size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <path d="M8 6L16 16L8 26" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16 6L24 16L16 26" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.6" />
  </svg>
);

// Feature card for left panel
const FeatureCard = ({ icon, title, description }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 2,
    p: '1rem 1.25rem', bgcolor: colors.sidebarHover,
    border: '1px solid rgba(255,255,255,0.05)', borderRadius: '12px',
    transition: 'all 0.25s ease',
    '&:hover': { bgcolor: 'rgba(59,130,246,0.1)', borderColor: 'rgba(59,130,246,0.2)', transform: 'translateX(4px)' },
  }}>
    <Box sx={{
      width: 42, height: 42, bgcolor: 'rgba(59,130,246,0.15)', borderRadius: '10px',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: colors.accent,
    }}>
      {icon}
    </Box>
    <Box>
      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: colors.sidebarText, mb: 0.25, fontFamily }}>{title}</Typography>
      <Typography sx={{ fontSize: '0.875rem', color: colors.sidebarTextMuted, lineHeight: 1.5, fontFamily }}>{description}</Typography>
    </Box>
  </Box>
);

// Left panel content per account type
const brandingContent = {
  individual: {
    headline: <>Almost There!<br /><Box component="span" sx={{ color: colors.accent }}>Set Up Your Profile</Box></>,
    description: "Complete your profile to unlock your personal AI assistant. Build chatbots, automate workflows, and create intelligent tools.",
    features: [
      { icon: <RocketLaunchOutlinedIcon sx={{ fontSize: 20 }} />, title: "Quick Setup", description: "Get your first AI agent running in minutes" },
      { icon: <AutoAwesomeOutlinedIcon sx={{ fontSize: 20 }} />, title: "AI-Powered Tools", description: "Chatbots, courses, and automation built in" },
    ],
  },
  business: {
    headline: <>Almost There!<br /><Box component="span" sx={{ color: colors.accent }}>Configure Your Team</Box></>,
    description: "Complete setup to deploy intelligent AI agents across your team, engage customers 24/7, and scale operations globally.",
    features: [
      { icon: <GroupsOutlinedIcon sx={{ fontSize: 20 }} />, title: "Team Collaboration", description: "Invite your team and manage roles & permissions" },
      { icon: <TrendingUpOutlinedIcon sx={{ fontSize: 20 }} />, title: "Scale Operations", description: "Multi-channel support across 52+ languages" },
    ],
  },
};

// --- Main Component ---
export default function SignUpSteps() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const theme = useTheme();

    // State
    const [isLoading, setIsLoading] = useState(true);
    const [isProcessingStep, setIsProcessingStep] = useState(false);
    const [profileFormValid, setProfileFormValid] = useState(false);
    const [error, setError] = useState(null);
    const [activeStep, setActiveStep] = useState(0);
    const [userRole, setUserRole] = useState(null);
    const [steps, setSteps] = useState([]);
    const [accountType, setAccountType] = useState('individual');

    // Refs
    const profilePageRef = useRef(null);

    // --- localStorage Key ---
    let SETUP_COMPLETE_KEY = SETUP_COMPLETE_LOCAL_KEY;

    // --- Dynamic Step Definitions based on User Role ---
    const getStepsForRole = (role) => {
        const isAdmin = role?.toLowerCase() === "admin";

        if (isAdmin) {
            return [
                { label: 'Profile Settings', slug: 'profile', path: '/update-profile' },
                { label: 'Accept Terms', slug: 'terms', path: '/accept-terms' },
                { label: 'Select Plan', slug: 'plan', path: '/select-plan' },
            ];
        } else {
            return [
                { label: 'Complete Your Profile', slug: 'profile', path: '/update-profile' },
                { label: 'Accept Terms', slug: 'terms', path: '/accept-terms' },
            ];
        }
    };

    const getStepIndexFromSlug = (slug, stepsArray) => {
        const index = stepsArray.findIndex(step => step.slug === slug);
        return index === -1 ? 0 : index;
    };

    const getSlugFromPath = (pathname, stepsArray) => {
        const step = stepsArray.find(s => s.path === pathname);
        return step ? step.slug : null;
    };

    const determineRequiredStepSlug = (userData, stepsArray) => {
        if (!userData || !stepsArray.length) return null;

        const isAdmin = userData?.active_team?.user_role_in_team?.toLowerCase() === "admin";

        if (!userData.profile) {
            return stepsArray[0].slug;
        }

        if (userData.active_team?.has_pending_terms) {
            return stepsArray[1].slug;
        }

        if (isAdmin && !userData.active_team?.subscription) {
            return stepsArray[2].slug;
        }

        return null;
    };

    // --- Effect 1: Initial Authentication & Role Detection ---
    useEffect(() => {
        setIsLoading(true);
        setError(null);

        let userData = {};
        const userStr = localStorage.getItem('user');
        if (userStr) {
            userData = JSON.parse(userStr);
        }

        if (!userData) {
            console.log("Effect 1: Not authenticated, redirecting to Login.");
            navigate('/Login', { replace: true });
            return;
        }

        // Read account type
        const acctType = userData?.account_type || 'individual';
        setAccountType(acctType);

        const role = userData?.active_team?.user_role_in_team;
        setUserRole(role);
        const roleSteps = getStepsForRole(role);
        setSteps(roleSteps);

        const isSetupCompleteFlag = localStorage.getItem(SETUP_COMPLETE_KEY) === 'true';
        const requiredSlugCheck = determineRequiredStepSlug(userData, roleSteps);

        if (isSetupCompleteFlag || requiredSlugCheck === null) {
            console.log("Effect 1: Setup complete, redirecting to Dashboard.");
            window.location.href = getDefaultRoute();
            return;
        }

        console.log("Effect 1: Auth & role checks passed. User role:", role);
        setIsLoading(false);

    }, [navigate]);

    // --- Effect 2: Enforce Required Step & Synchronize UI ---
    useEffect(() => {
        if (isLoading || !steps.length) return;

        console.log("Effect 2: Running step enforcement and UI sync.");
        setError(null);

        let userData = {};
        const userStr = localStorage.getItem('user');
        if (userStr) {
            userData = JSON.parse(userStr);
        }

        const requiredSlug = determineRequiredStepSlug(userData, steps);

        if (requiredSlug === null) {
            const isSetupCompleteFlag = localStorage.getItem(SETUP_COMPLETE_KEY) === 'true';
            if(isSetupCompleteFlag || requiredSlug === null){
                console.log("Effect 2: Data indicates completion, redirecting to Dashboard.");
                window.location.href = getDefaultRoute();
                return;
            }
        }

        const currentSlugInUrl = searchParams.get('step')?.toLowerCase();

        if (!currentSlugInUrl || getStepIndexFromSlug(currentSlugInUrl, steps) === -1 || currentSlugInUrl !== requiredSlug) {
            if (currentSlugInUrl !== requiredSlug) {
                console.warn(`Effect 2: URL step ('${currentSlugInUrl}') doesn't match required ('${requiredSlug}'). Forcing navigation.`);
                setSearchParams({ step: requiredSlug }, { replace: true });
                return;
            }
        }

        console.log(`Effect 2: URL step ('${currentSlugInUrl}') matches required ('${requiredSlug}'). Syncing UI.`);
        const currentStepIndex = getStepIndexFromSlug(requiredSlug, steps);
        setActiveStep(currentStepIndex);

        setIsProcessingStep(false);
        setIsLoading(false);

    }, [searchParams, setSearchParams, steps, isLoading]);

    // --- Effect 3: Set Document Title ---
    useEffect(() => {
        document.title = 'Account Setup | KlonIT';
    }, []);

    // --- Callbacks & Handlers ---
    const handleStepCompletion = useCallback((nextStepUrl) => {
        setIsProcessingStep(false);
        setError(null);
        console.log("HandleCompletion: Received completion URL:", nextStepUrl);

        const isAdmin = userRole?.toLowerCase() === "admin";
        console.log(nextStepUrl)
        if (nextStepUrl === "/chatbots") {
            localStorage.setItem(SETUP_COMPLETE_KEY, 'true');
            window.location.href = getDefaultRoute();
        } else {
            const nextSlug = getSlugFromPath(nextStepUrl, steps);
            if (nextSlug) {
                console.log(`HandleCompletion: Mapped URL ${nextStepUrl} to slug ${nextSlug}.`);
                setSearchParams({ step: nextSlug });
            } else {
                console.warn("HandleCompletion: Unknown next_step URL, assuming completion:", nextStepUrl);
                localStorage.setItem(SETUP_COMPLETE_KEY, 'true');
                window.location.href = getDefaultRoute();
            }
        }
    }, [setSearchParams, steps, userRole]);

    const handleStepError = useCallback((errorMessage) => {
        setIsProcessingStep(false);
        setError(errorMessage || "An error occurred in the current step.");
    }, []);

    const handleNext = async () => {
        setError(null);
        setIsProcessingStep(true);
        const currentStepIndex = activeStep;
        const currentStep = steps[currentStepIndex];
        const isAdmin = userRole?.toLowerCase() === "admin";

        if (currentStep.slug === 'profile') {
            if (profilePageRef.current?.handleSaveButtonClick) {
                profilePageRef.current.handleSaveButtonClick();
            } else {
                handleStepError("Could not initiate profile save.");
            }
        } else if (currentStep.slug === 'terms') {
            setIsProcessingStep(false);
        } else if (currentStep.slug === 'plan' && isAdmin) {
            setIsProcessingStep(false);
        }
    };

    const handleBack = () => {
        if (activeStep > 0) {
            const previousStepSlug = steps[activeStep - 1].slug;
            setSearchParams({ step: previousStepSlug });
        }
    };

    const handleLogout = () => {
        localStorage.clear();
        navigate("/log-out");
    };

    const handleProfileFormValidity = (isValid) => setProfileFormValid(isValid);

    const getStepContent = (stepIndex) => {
        if (stepIndex < 0 || stepIndex >= steps.length) {
            console.error(`getStepContent called with invalid index: ${stepIndex}`);
            return <Typography color="error">Invalid Step</Typography>;
        }

        const isAdmin = userRole?.toLowerCase() === "admin";
        const currentStep = steps[stepIndex];

        if (currentStep.slug === 'profile') {
            return <ProfilePage
                ref={profilePageRef}
                onFormValidityChange={handleProfileFormValidity}
                onComplete={handleStepCompletion}
                onError={handleStepError}
                includePasswordUpdate={!isAdmin}
                isSetupMode={true}
            />;
        } else if (currentStep.slug === 'terms') {
            return <TermsAcceptanceStep
                isAdmin={isAdmin}
                onComplete={handleStepCompletion}
                onError={handleStepError}
                isSetupMode={true}
            />;
        } else if (currentStep.slug === 'plan' && isAdmin) {
            return <PricingPage
                onComplete={handleStepCompletion}
                onError={handleStepError}
                isSetupMode={true}
            />;
        } else {
            return <Typography>Loading Step...</Typography>;
        }
    };

    // Get branding content based on account type
    const branding = brandingContent[accountType] || brandingContent.individual;

    // --- Render Logic ---
    if (isLoading && !error) {
        return (
            <Box sx={{
                display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh',
                background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 40%, #e0e7ff 70%, #f0f4ff 100%)',
                fontFamily,
            }}>
                <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size={40} sx={{ color: theme.palette.primary.main }} />
                    <Typography sx={{ mt: 2, fontFamily, color: colors.textSecondary, fontSize: '0.9rem' }}>
                        Checking setup status...
                    </Typography>
                </Box>
            </Box>
        );
    }

    if (error && !steps.length) {
        return (
            <Box sx={{
                display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
                minHeight: '100vh', gap: 2, fontFamily,
                background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 40%, #e0e7ff 70%, #f0f4ff 100%)',
            }}>
                <Alert severity="error" sx={{ fontFamily, borderRadius: '14px' }}>
                    Error during setup: {error}
                </Alert>
                <Button
                    variant="contained"
                    onClick={handleLogout}
                    sx={{
                        fontFamily, textTransform: 'none', borderRadius: '14px',
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        px: 4, py: 1.25,
                    }}
                >
                    Logout
                </Button>
            </Box>
        );
    }

    const isAdmin = userRole?.toLowerCase() === "admin";
    const setupTitle = isAdmin ? "Account Setup" : "Complete Your Profile";

    return (
        <Box sx={{ fontFamily, height: '100vh', display: 'flex', overflow: 'hidden' }}>
            {/* Font import */}
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700&display=swap');`}</style>

            {/* ===== Left Branding Panel (Fixed) ===== */}
            <Box sx={{
                display: { xs: 'none', lg: 'flex' }, width: '42%', minWidth: '380px',
                flexDirection: 'column', justifyContent: 'center', p: '3rem',
                position: 'fixed', top: 0, left: 0, bottom: 0,
                overflow: 'hidden',
                backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.85), rgba(15, 23, 42, 0.9)), url(${brandingBg})`,
                backgroundSize: 'cover', backgroundPosition: 'center',
                '&::before': {
                    content: '""', position: 'absolute', inset: 0, pointerEvents: 'none',
                    background: 'radial-gradient(circle at 20% 30%, rgba(59,130,246,0.12) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(59,130,246,0.08) 0%, transparent 40%)',
                },
            }}>
                <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 420, mx: 'auto' }}>
                    {/* Logo */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.875rem', mb: 3 }}>
                        <Box sx={{
                            width: 48, height: 48, bgcolor: colors.accent, borderRadius: '12px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
                        }}>
                            <KlonITLogo />
                        </Box>
                        <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: '#fff', letterSpacing: '-0.025em', fontFamily }}>
                            KlonIT
                        </Typography>
                    </Box>

                    {/* Pill */}
                    <Box sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                        px: 2, py: 0.5, bgcolor: colors.sidebarHover,
                        border: '1px solid rgba(255,255,255,0.08)', borderRadius: '100px',
                        fontSize: '0.8125rem', fontWeight: 600, color: colors.accent, mb: 2, letterSpacing: '0.05em', fontFamily,
                    }}>
                        <BoltIcon sx={{ fontSize: 14 }} />
                        CLONE · SCALE · GROW
                    </Box>

                    {/* Headline */}
                    <Typography sx={{
                        fontSize: '2.5rem', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.03em',
                        color: '#fff', mb: 1.5, fontFamily,
                    }}>
                        {branding.headline}
                    </Typography>

                    {/* Description */}
                    <Typography sx={{ fontSize: '1rem', lineHeight: 1.7, color: colors.sidebarTextMuted, mb: 2.5, fontFamily }}>
                        {branding.description}
                    </Typography>

                    {/* Feature Cards */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
                        {branding.features.map((f, i) => (
                            <FeatureCard key={i} icon={f.icon} title={f.title} description={f.description} />
                        ))}
                    </Box>

                    {/* Step indicator at bottom */}
                    <Box sx={{ mt: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ShieldOutlinedIcon sx={{ fontSize: 14, color: colors.success }} />
                        <Typography sx={{ fontSize: '0.75rem', color: colors.sidebarTextMuted, fontFamily }}>
                            Step {activeStep + 1} of {steps.length} — {steps[activeStep]?.label}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* ===== Right Content Panel (Scrollable) ===== */}
            <Box sx={{
                flex: 1, display: 'flex', flexDirection: 'column',
                ml: { xs: 0, lg: '42%' }, height: '100vh',
                position: 'relative', overflowY: 'auto', overflowX: 'hidden',
                background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 40%, #e0e7ff 70%, #f0f4ff 100%)',
                '&::before': {
                    content: '""', position: 'absolute', top: '-30%', right: '-20%',
                    width: '500px', height: '500px', borderRadius: '50%', pointerEvents: 'none',
                    background: `radial-gradient(circle, ${theme.palette.primary.main}12 0%, transparent 70%)`,
                },
                '&::after': {
                    content: '""', position: 'absolute', bottom: '-20%', left: '-10%',
                    width: '400px', height: '400px', borderRadius: '50%', pointerEvents: 'none',
                    background: 'radial-gradient(circle, rgba(139,92,246,0.08) 0%, transparent 70%)',
                },
            }}>
                {/* Mobile Logo (hidden on desktop) */}
                <Box sx={{ display: { xs: 'flex', lg: 'none' }, alignItems: 'center', justifyContent: 'center', gap: '0.75rem', pt: 3, pb: 1 }}>
                    <Box sx={{ width: 44, height: 44, bgcolor: colors.textPrimary, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <KlonITLogo color="white" />
                    </Box>
                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: colors.textPrimary, letterSpacing: '-0.025em', fontFamily }}>KlonIT</Typography>
                </Box>

                <Box sx={{
                    maxWidth: '720px', width: '100%', mx: 'auto',
                    py: { xs: 2, md: 3 }, px: { xs: 2, md: 3 },
                    position: 'relative', zIndex: 1, flex: 1,
                    display: 'flex', flexDirection: 'column',
                }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
                        <Box sx={{ flex: 1, textAlign: 'center' }}>
                            <Typography sx={{
                                fontFamily, fontWeight: 700, fontSize: { xs: '1.35rem', md: '1.5rem' },
                                color: colors.textPrimary,
                                background: `linear-gradient(135deg, ${colors.textPrimary}, ${theme.palette.primary.main})`,
                                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                            }}>
                                {setupTitle}
                            </Typography>
                            <Typography sx={{ fontFamily, color: colors.textSecondary, fontSize: '0.85rem', mt: 0.5 }}>
                                Complete the steps below to get started
                            </Typography>
                        </Box>
                        <Button
                            onClick={handleLogout}
                            startIcon={<LogoutOutlinedIcon sx={{ fontSize: '1rem' }} />}
                            sx={{
                                fontFamily, textTransform: 'none', fontWeight: 500, fontSize: '0.8rem',
                                color: colors.textSecondary, borderRadius: '12px', px: 2, py: 0.75,
                                bgcolor: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)',
                                border: '1px solid rgba(0,0,0,0.06)',
                                minWidth: 'auto',
                                '&:hover': { bgcolor: 'rgba(220,38,38,0.1)', color: '#dc2626', borderColor: 'rgba(220,38,38,0.2)' },
                            }}
                        >
                            Logout
                        </Button>
                    </Box>

                    {/* Stepper */}
                    <Box sx={{
                        bgcolor: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(12px)',
                        borderRadius: '16px', p: { xs: 2, md: 2.5 }, mb: 2.5,
                        border: '1px solid rgba(255,255,255,0.6)',
                        boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                    }}>
                        <Stepper activeStep={activeStep} alternativeLabel sx={{
                            '& .MuiStepLabel-label': { fontFamily, fontSize: '0.8rem', fontWeight: 500 },
                            '& .MuiStepLabel-label.Mui-active': { fontWeight: 600, color: theme.palette.primary.main },
                            '& .MuiStepLabel-label.Mui-completed': { fontWeight: 600, color: colors.textPrimary },
                            '& .MuiStepIcon-root.Mui-active': { color: theme.palette.primary.main },
                            '& .MuiStepIcon-root.Mui-completed': { color: theme.palette.primary.main },
                            '& .MuiStepConnector-line': { borderColor: 'rgba(0,0,0,0.08)' },
                        }}>
                            {steps.map((step) => (
                                <Step key={step.slug}>
                                    <StepLabel>{step.label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Box>

                    {/* Error */}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2, fontFamily, borderRadius: '14px',
                            bgcolor: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(8px)',
                        }}>
                            {error}
                        </Alert>
                    )}

                    {/* Step Content */}
                    <Box sx={{
                        bgcolor: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(12px)',
                        borderRadius: '16px', p: { xs: 2, md: 3 },
                        border: '1px solid rgba(255,255,255,0.6)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
                        flex: 1,
                    }}>
                        {getStepContent(activeStep)}
                    </Box>

                    {/* Navigation Buttons - Only show for non-terms steps */}
                    {steps[activeStep]?.slug !== 'terms' && (
                        <Box sx={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            mt: 2.5, px: 1, pb: 2,
                        }}>
                            <Button
                                disabled={activeStep === 0 || isProcessingStep}
                                onClick={handleBack}
                                startIcon={<ArrowBackIcon sx={{ fontSize: '1rem' }} />}
                                sx={{
                                    fontFamily, textTransform: 'none', fontWeight: 500, fontSize: '0.875rem',
                                    color: colors.textSecondary, borderRadius: '14px', px: 3, py: 1,
                                    bgcolor: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)',
                                    border: '1px solid rgba(0,0,0,0.06)',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                                    '&:disabled': { opacity: 0.4 },
                                }}
                            >
                                Back
                            </Button>

                            {(activeStep < steps.length - 1) ? (
                                <Button
                                    onClick={handleNext}
                                    disabled={isProcessingStep || (activeStep === 0 && !profileFormValid)}
                                    endIcon={isProcessingStep
                                        ? <CircularProgress size={16} color="inherit" />
                                        : <ArrowForwardIcon sx={{ fontSize: '1rem' }} />
                                    }
                                    sx={{
                                        fontFamily, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem',
                                        color: '#fff', borderRadius: '14px', px: 4, py: 1.25,
                                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                        boxShadow: `0 4px 16px ${theme.palette.primary.main}40`,
                                        '&:hover': {
                                            background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                            boxShadow: `0 6px 20px ${theme.palette.primary.main}50`,
                                            transform: 'translateY(-1px)',
                                        },
                                        '&:disabled': { opacity: 0.5, transform: 'none' },
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    {isProcessingStep ? "Saving..." : "Save & Continue"}
                                </Button>
                            ) : (
                                isAdmin && steps[activeStep]?.slug === 'plan' && (
                                    <Typography sx={{
                                        fontFamily, fontStyle: 'italic', textAlign: 'right',
                                        fontSize: '0.8rem', color: colors.textTertiary,
                                    }}>
                                        Select a plan above to complete setup.
                                    </Typography>
                                )
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
